<template>
  <div>
    <b-card>

      <div class="d-flex justify-content-between mb-3 align-items-center">
        <h3 class="mb-0">Customers</h3>
        <div class="d-flex gap-2">
          <input
            ref="bulkUploadInput"
            type="file"
            accept=".csv,.xlsx,.xls"
            class="d-none"
            @change="handleBulkUpload"
          />
          <b-button
            variant="outline-info"
            size="sm"
            class="mr-1"
            @click="downloadSample"
          >
            <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
            Sample CSV
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-1"
            @click="$refs.bulkUploadInput.click()"
          >
            <feather-icon icon="UploadIcon" size="16" class="mr-1" />
            Bulk Upload
          </b-button>
          <b-button variant="primary" size="sm" @click="openCreate">
            <feather-icon icon="PlusIcon" size="16" class="mr-1" />
            Create Customer
          </b-button>
        </div>
      </div>

      <b-table
        :items="customers"
        :fields="fields"
        responsive
        small
      >

        <template #cell(name)="row">
          {{ row.item.first_name }} {{ row.item.last_name }}
        </template>

        <template #cell(state_data)="row">
          {{ row.item.state_data ? row.item.state_data.name : "" }}
        </template>

        <template #cell(manage)="row">
          <b-button size="sm" variant="info" @click="editCustomer(row.item)">
            Edit
          </b-button>

          <b-button
            size="sm"
            variant="danger"
            class="ml-1"
            @click="deleteCustomerConfirm(row.item)"
          >
            Delete
          </b-button>
        </template>

      </b-table>

      <b-pagination
        v-model="currentPage"
        :total-rows="totalItems"
        :per-page="perPage"
        align="right"
        class="mt-3"
      />

    </b-card>

    <CreateCustomerModal
      @modalClosed="fetchCustomers"
      :key="createKey"
    />

    <EditCustomerModal
      :customer="customer"
      @modalClosed="fetchCustomers"
      :key="editKey"
    />

  </div>
</template>

<script>
import { mapActions } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import CreateCustomerModal from "@/components/customer/CreateCustomerModal.vue";
import EditCustomerModal from "@/components/customer/EditCustomerModal.vue";

export default {
  components: {
    CreateCustomerModal,
    EditCustomerModal,
  },

  data() {
    return {
      fields: [
        { key: "name", label: "Name" },
        { key: "phone", label: "Phone" },
        { key: "email", label: "Email" },
        { key: "state_data", label: "State" },
        { key: "manage", label: "Manage" },
      ],
      customers: [],
      currentPage: 1,
      totalItems: 0,
      perPage: 0,
      customer: null,
      createKey: 0,
      editKey: 0,
    };
  },

  async mounted() {
    await this.fetchCustomers();
  },

  methods: {
    ...mapActions("appData", [
      "getCustomers",
      "deleteCustomer",
      "bulkUploadCustomers",
      "downloadSampleCsv",
    ]),

    async fetchCustomers() {
      const res = await this.getCustomers({
        page: this.currentPage,
      });

      this.customers = res.data.results;
      this.totalItems = res.data.count;
      this.perPage = res.data.per_page;
    },

    openCreate() {
      this.createKey++;
      this.$nextTick(() =>
        this.$bvModal.show("create-customer-modal")
      );
    },

    async handleBulkUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      
      event.target.value = '';
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await this.bulkUploadCustomers(formData);
        const data = response.data;
        const errorCount = data.errors ? data.errors.length : 0;
        
        if (errorCount > 0) {
          const errorText = data.errors.map(e => `Row ${e.row}: ${e.error}`).join('\n');
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: `Created ${data.created} customers. ${errorCount} errors.`,
                text: errorText,
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            },
            {
              position: "top-right",
            }
          );
        } else {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "Bulk Upload Successful",
                text: `${data.created} customers created successfully.`,
                icon: "CheckCircleIcon",
                variant: "success",
              },
            },
            {
              position: "top-right",
            }
          );
        }
        await this.fetchCustomers();
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || "Upload failed.";
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Bulk Upload Failed",
              text: msg,
              icon: "XCircleIcon",
              variant: "danger",
            },
          },
          {
            position: "top-right",
          }
        );
      }
    },

    async downloadSample() {
      try {
        const response = await this.downloadSampleCsv();
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'customers_sample.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Download Started",
              text: "Sample CSV template downloaded.",
              icon: "DownloadIcon",
              variant: "info",
            },
          },
          {
            position: "top-right",
          }
        );
      } catch (error) {
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "Download Failed",
              text: error.message || "Unable to download sample CSV.",
              icon: "XCircleIcon",
              variant: "danger",
            },
          },
          {
            position: "top-right",
          }
        );
      }
    },

    editCustomer(customer) {
      this.customer = customer;
      this.editKey++;
      this.$nextTick(() =>
        this.$bvModal.show("edit-customer-modal")
      );
    },

    async deleteCustomerConfirm(customer) {
      if (confirm("Are you sure you want to delete this customer?")) {
        await this.deleteCustomer({ pk: customer.id });
        await this.fetchCustomers();
      }
    },
  },

  watch: {
    currentPage() {
      this.fetchCustomers();
    },
  },
};
</script>